import React, { useContext, useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Box,
  CircularProgress,
} from '@mui/material';
import { Teacher } from '@kimuno/api-sdk/lib/types/responses/Teacher';
import TeacherItem, {
  ResendActionType
} from '../SchoolTeacherItem/TeacherItem';
import schoolService from '../../../../services/school.service';
import { SnackbarContext } from '../../../../state/SnackbarContextProvider';
import { useSelector } from 'react-redux';
import { selectSelectedSchoolId, selectUser } from '../../../../reducers/user.selectors';
import { routes } from '../../../../constants';
import useRouter from '../../../../utils/useRouter';
import { useTranslation } from 'react-i18next';
import services from '../../../../services';
import { TeacherInviteResent } from '../../../../ampli';

interface TeachersListProps {
  teachersList: Teacher[];
  loading: boolean;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: unknown, newPage: number) => void;
  onRefreshTeachers: () => void;
  showChangeUserTypeButton: boolean;
  schoolSeats: any;
  totalCount: number;
}

const TeachersList: React.FC<TeachersListProps> = ({
  teachersList,
  loading,
  page,
  rowsPerPage,
  onPageChange,
  onRefreshTeachers,
  showChangeUserTypeButton,
  schoolSeats,
  totalCount
}) => {
  const user = useSelector(selectUser);
  const { router } = useRouter();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRowIndex, setMenuRowIndex] = useState<number | null>(null);
  const menuOpen = Boolean(anchorEl);
  const { openSnackbar } = useContext(SnackbarContext);
  const selectedSchoolId = useSelector(selectSelectedSchoolId);
  const { t } = useTranslation();

  useEffect(() => {
    if (user && !user.isSchoolAdmin) {
      router.push(`/${routes.dashboard.root}`);
    }
  }, [user?.isSchoolAdmin]);

  const showError = (message:string) => {
    openSnackbar({
      type: 'error',
      message,
    });
  };

  const showInfo = (message:string) => {
    openSnackbar({
      type: 'success',
      message,
    });
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, rowIndex: number) => {
    setAnchorEl(event.currentTarget);
    setMenuRowIndex(rowIndex);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowIndex(null);
  };

  const handleActionsMenuItemClick = async (licenseId: number, teacher: Teacher) => {
    try {
      if (!selectedSchoolId) return;
      const result = await schoolService.upgradeSubscription(
        selectedSchoolId,
        teacher.accountId,
        licenseId > 0 ? licenseId : 557,
        teacher.subscriptions[0].id,
        teacher.userId
      );

      if (result?.success) {
        onRefreshTeachers();
        showInfo(t('Subscription updated successfully'));
      } else {
        showError(t('Error updating subscription'));
      }
    } catch (error) {
      showError(t('Error updating subscription'));
    } finally {
      handleMenuClose();
    }
  };

  const handleResendMenuItemClick = async (actionType: ResendActionType, teacher: Teacher) => {
    try {
      let result;
      let message;

      if (!selectedSchoolId) {
        return;
      }

      if (actionType === ResendActionType.RESEND_INVITATION) {
        result = await schoolService.resendInvitation(
          selectedSchoolId,
          teacher.userId,
          +teacher?.subscriptions[0]?.licenceModelId || 557
        );

        if (result?.success === true && user?.isSchoolAdmin && selectedSchoolId && teacher.userId) {
          services.track.ampliEventTrack({
            event: new TeacherInviteResent({
              'school id': selectedSchoolId,
              'teacher user id': teacher.userId,
            })
          });
        }

        message = t('Invitation email sent successfully');
      } else {
        // Reset password
        result = await schoolService.resetPassword(selectedSchoolId, teacher.email);
        message = t('Password reset email sent successfully');
      }

      if (result?.success === true) {
        showInfo(message);
      } else {
        showError(t('Error while sending an email'));
      }
    } catch (error) {
      showError(t('Error while sending an email'));
    } finally {
      handleMenuClose();
    }
  };

  const handleAssignSchoolAdminClick = async () => {
    onRefreshTeachers();
  };

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='300px'>
        <CircularProgress />
      </Box>
    );
  }

  if (teachersList.length === 0) {
    return null;
  }

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('Name').toUpperCase()}</TableCell>
              <TableCell>{t('Email').toUpperCase()}</TableCell>
              <TableCell>{t('Status').toUpperCase()}</TableCell>
              <TableCell>{t('Subscription Type').toUpperCase()}</TableCell>
              <TableCell>{t('Actions').toUpperCase()}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {teachersList.map((teacher, index) => (
              <TeacherItem
                key={teacher.accountId}
                teacher={teacher}
                index={index}
                onMenuClick={handleMenuClick}
                onActionsMenuItemClick={handleActionsMenuItemClick}
                onResendMenuItemClick={handleResendMenuItemClick}
                onAssignSchoolAdminClick={handleAssignSchoolAdminClick}
                anchorEl={menuRowIndex === index ? anchorEl : null}
                menuOpen={menuRowIndex === index ? menuOpen : false}
                onMenuClose={handleMenuClose}
                showChangeUserTypeButton={showChangeUserTypeButton}
                schoolSeats={schoolSeats}
                onTeacherDeleted={() => {}}
                onTeacherUpdated={onRefreshTeachers}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component='div'
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        rowsPerPageOptions={[rowsPerPage]}
      />
    </>
  );
};

export default TeachersList;
